/* eslint-disable react/jsx-no-useless-fragment */

import { AccelFile, UploadFileArgs } from "@axl-dev/school-sdk";
import { notification } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

import { useDrawer, useLoc, useSdk } from "@app/shared/lib/hooks";
import { saveAs } from "@app/shared/lib/save-as";

import { FileGridItem } from "./FileGridItem/FileGridItem";
import { FileListItem } from "./FileListItem/FileListItem";

import styles from "./FileList.module.scss";

export declare type FileIconColor = "#ffffff" | "#f1f1f1";
export declare type FileListViewType = "list" | "grid";
export declare type FileListSize = "xs" | "sm" | "md" | "lg" | "xl";

export type FilesProps = {
  files: AccelFile[];
  /**
   * Can edit filename
   */
  editable?: boolean;
  downloadable?: boolean;
  draggable?: boolean;
  uploadArgs?: UploadFileArgs;
  view?: FileListViewType;
  size?: FileListSize;
  deletable?: boolean;
  replacable?: boolean;
  dateCreatedVisible?: boolean;
  descriptionVisible?: boolean;
  beforeUpload?: (file: AccelFile) => Promise<void | boolean>;
  beforeRemove?: (file: AccelFile) => Promise<void | boolean>;
  onRemoved?: (file: AccelFile) => void;
  onAborted?: (file: AccelFile) => void;
  onUploaded?: (file: AccelFile) => void;
  onDownload?: (file: AccelFile) => void;
  onDrop?: (srcIndex: number, dstIndex: number) => void;
  removeFromServer?: boolean;
  acceptFiles?: string;
  fileIconColor?: FileIconColor;
  onFileUpdate?: (file: AccelFile) => void;
};

export type FileProps = {
  //   fileProvider: FileProvider;
  file: AccelFile;
  index: number;
  size: FileListSize;
  editable: boolean;
  deletable: boolean;
  downloadable: boolean;
  draggable: boolean;
  uploadArgs: UploadFileArgs;
  replaceable: boolean;
  descriptionVisible: boolean;
  dateCreatedVisible: boolean;
  fileIconColor: FileIconColor;
  beforeUpload?: (file: AccelFile) => Promise<void | boolean>;
  onUploaded?: (file: AccelFile) => void;
  onAborted?: (file: AccelFile) => void;
  onRemove?: (file: AccelFile) => void;
  onChange?: (file: AccelFile, changes: Partial<AccelFile>) => Promise<boolean>;
  onReplace?: (file: AccelFile) => void;
  onDownload?: (file: AccelFile) => void;
  onPreview: (file: AccelFile) => void;
};

export const Files: React.FC<FilesProps> = ({
  beforeRemove,
  removeFromServer = true,
  onRemoved,
  onDownload,
  editable = true,
  downloadable = true,
  deletable = true,
  uploadArgs = {},
  size = "md",
  replacable = false,
  dateCreatedVisible = false,
  descriptionVisible = false,
  draggable = false,
  fileIconColor,
  onAborted,
  onUploaded,
  beforeUpload,
  view = "list",
  files = [],
  onDrop,
  acceptFiles,
  onFileUpdate,
}) => {
  const sdk = useSdk();
  const loc = useLoc();

  const fileReplaceInput = useRef<HTMLInputElement>(null);
  const [fileToReplace, setFileToReplace] = useState<AccelFile | null>(null);

  const previewModal = useDrawer<AccelFile>();

  const remove = useCallback(
    async (file: AccelFile) => {
      if (beforeRemove != null)
        if ((await beforeRemove(file)) === false) return;
      if (removeFromServer === true) {
        const res = await sdk.api.file.remove(file.id);
        if (res.success) onRemoved?.(file);
      } else {
        onRemoved?.(file);
      }
    },
    [beforeRemove, onRemoved, removeFromServer, sdk.api.file],
  );

  const save = useCallback(
    async (file: AccelFile, changes: Partial<AccelFile>) => {
      const res = await sdk.api.file.save({ ...changes, id: file.id });
      file.update(changes);
      onFileUpdate?.(new AccelFile(file));

      return res.success;
    },
    [onFileUpdate, sdk.api.file],
  );

  const replaceFile = useCallback(
    (nativeFile: File) => {
      if (!fileToReplace) return;

      const file = AccelFile.fromFile(nativeFile);
      if (file.extension !== fileToReplace.extension) {
        setFileToReplace(null);
        notification.error({
          message: loc.word("ErrorFileReplaceExtensionError", {
            default:
              "The file extensions to replace must match the extension of the original file",
          }),
          duration: 3,
        });

        return;
      }

      fileToReplace.replaceFile(nativeFile);
      setFileToReplace(null);
    },
    [fileToReplace, loc],
  );

  const previewFile = useCallback(
    async (file: AccelFile) => {
      if (file.isImage) {
        previewModal.open(file);

        return;
      }

      // store.preview(file);
      const result = await sdk.api.file.download(file.id, true);
      if (result.success) {
        saveAs(result.body, true, file.filename);
      }
    },
    [previewModal, sdk.api.file],
  );

  const downloadFile = useCallback(
    async (file: AccelFile) => {
      // await store.download(file);
      if (file.isExternalFile) {
        saveAs(file.url!, true, file.filename);

        return;
      }

      const result = await sdk.api.file.download(file.id);
      if (result.success) {
        saveAs(result.body, false, file.filename);
        onDownload?.(file);
      }
      // return result.success;
    },
    [onDownload, sdk.api.file],
  );

  const fileProps = useMemo<Omit<FileProps, "file" | "index">>(
    () => ({
      //   fileProvider: ctx.fileProvider,
      editable: editable ?? true,
      downloadable: downloadable ?? true,
      uploadArgs: uploadArgs ?? {},
      size: size!,
      deletable: deletable ?? true,
      replaceable: replacable ?? false,
      dateCreatedVisible: dateCreatedVisible ?? false,
      descriptionVisible: descriptionVisible ?? false,
      draggable: draggable ?? false,
      fileIconColor: fileIconColor ?? "#ffffff",
      onRemove: (file) => remove(file),
      onAborted,
      onUploaded,
      onChange: (file, changes) => save(file, changes),
      beforeUpload,
      onDownload: (file) => downloadFile(file),
      onReplace: (file) => {
        setFileToReplace(file);

        // dirty hack (apply file ext for file input accept)
        setTimeout(() => {
          fileReplaceInput.current?.click();
        }, 50);
      },
      onPreview: (file) => previewFile(file),
    }),
    [
      beforeUpload,
      dateCreatedVisible,
      deletable,
      descriptionVisible,
      downloadFile,
      downloadable,
      draggable,
      editable,
      fileIconColor,
      onAborted,
      onUploaded,
      previewFile,
      remove,
      replacable,
      save,
      size,
      uploadArgs,
    ],
  );

  return (
    <>
      <div className={styles.fl} data-view={view}>
        {view === "grid" ? (
          files.map((x, i) => (
            <FileGridItem key={x.key} file={x} index={i} {...fileProps} />
          ))
        ) : (
          <>
            {draggable ? (
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;
                  onDrop?.(result.source.index, result.destination.index);
                }}
              >
                <Droppable
                  droppableId="fl_droppable"
                  isDropDisabled={!draggable}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {files.map((x, i) => (
                        <FileListItem
                          key={x.key}
                          file={x}
                          index={i}
                          {...fileProps}
                        />
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              files.map((x, i) => (
                <FileListItem key={x.key} file={x} index={i} {...fileProps} />
              ))
            )}
          </>
        )}
      </div>

      {replacable && (
        <input
          ref={fileReplaceInput}
          type="file"
          accept={fileToReplace?.extension ?? acceptFiles}
          className="d-none"
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!e.target.files || e.target.files.length === 0) return;
            replaceFile(e.target.files[0]!);
          }}
        />
      )}
    </>
  );
};
